import { createAnalysisState } from '@aibi/analysis-engine';
import {
  FILTER_OPERATOR_ARITY,
  type AIAction,
  type AnalysisDimension,
  type AnalysisState,
} from '@aibi/contracts';
import {
  isCalculatedDimension,
  isCalculatedMeasure,
  isTemporalDimension,
  type ModelOperation,
  type SemanticModelIndex,
} from '@aibi/semantic-model';
import { err, ok, type Result } from '@aibi/shared';
import type { AIDiagnostic } from '../errors.js';
import { pruneReferences } from './prune-references.js';

type ModelActionType = 'createMeasure' | 'createDimension' | 'createCalculatedColumn';
type InformationalAction = Extract<
  AIAction,
  { type: 'narrative' | 'requestExplanation' | 'suggestVisualization' }
>;

/**
 * Cambio propuesto al Semantic Model. La IA nunca modifica el modelo: la propuesta
 * incluye la operacion del motor semantico para aplicarla solo tras confirmacion explicita.
 */
export interface ModelChangeProposal {
  readonly actionId: string;
  readonly actionType: ModelActionType;
  readonly objectId: string;
  readonly objectLabel: string;
  readonly operation: ModelOperation;
  readonly requiresConfirmation: true;
}

export interface AppliedAIActions {
  /** Estado resultante; es la misma referencia que la entrada si ninguna accion lo cambia. */
  readonly state: AnalysisState;
  /** Acciones que modificaron el Analysis State, en orden. */
  readonly stateActionIds: readonly string[];
  readonly modelChanges: readonly ModelChangeProposal[];
  readonly informational: readonly InformationalAction[];
}

/** Clave local valida (`FieldKeySchema`) y unica derivada del id del objeto del modelo. */
export function allocateFieldKey(objectId: string, taken: ReadonlySet<string>): string {
  const tail = objectId.slice(objectId.lastIndexOf('.') + 1);
  let base = tail.replace(/[^A-Za-z0-9_]/g, '_').slice(0, 56);
  if (!/^[A-Za-z_]/.test(base)) base = `f_${base}`;
  let candidate = base;
  for (let ordinal = 2; taken.has(candidate); ordinal++) candidate = `${base}_${String(ordinal)}`;
  return candidate;
}

class ActionFailure extends Error {}

/**
 * Aplica un lote de AI Actions a una COPIA del Analysis State de forma pura y atomica:
 * si cualquier accion falla, devuelve el diagnostico de esa accion y el estado original
 * no cambia. Las acciones de modelo se convierten en propuestas y las informativas no
 * modifican el estado.
 *
 * @example
 * const applied = applyAIActions(card, actions, index);
 * if (applied.ok) preview(applied.value.state); // card sigue intacto
 */
export function applyAIActions(
  state: AnalysisState,
  actions: readonly AIAction[],
  index: SemanticModelIndex,
): Result<AppliedAIActions, AIDiagnostic[]> {
  let working = state;
  const stateActionIds: string[] = [];
  const modelChanges: ModelChangeProposal[] = [];
  const informational: InformationalAction[] = [];

  for (const [position, action] of actions.entries()) {
    try {
      const next = step(working, action, index, modelChanges, informational);
      if (next !== working) {
        working = next;
        stateActionIds.push(action.id);
      }
    } catch (error) {
      if (!(error instanceof ActionFailure)) throw error;
      return err([
        {
          stage: 'sandbox',
          code: 'STATE_INVALID',
          path: `actions.${String(position)}`,
          message: error.message,
          actionId: action.id,
        },
      ]);
    }
  }
  return ok({
    state: stateActionIds.length === 0 ? state : pruneReferences(working),
    stateActionIds,
    modelChanges,
    informational,
  });
}

function step(
  state: AnalysisState,
  action: AIAction,
  index: SemanticModelIndex,
  modelChanges: ModelChangeProposal[],
  informational: InformationalAction[],
): AnalysisState {
  const fail = (message: string): never => {
    throw new ActionFailure(message);
  };
  const keys = (): Set<string> =>
    new Set([...state.dimensions.map((d) => d.key), ...state.measures.map((m) => m.key)]);
  const dimensionKey = (dimensionId: string): string =>
    state.dimensions.find((d) => d.dimensionId === dimensionId)?.key ??
    fail(`La dimension "${dimensionId}" no forma parte del analisis.`);
  const measureKey = (measureId: string): string =>
    state.measures.find((m) => m.measureId === measureId)?.key ??
    fail(`La medida "${measureId}" no forma parte del analisis.`);

  switch (action.type) {
    case 'addDimension': {
      const { dimensionId, key, timeGrain } = action.payload;
      const resolved =
        index.resolveDimension(dimensionId) ?? fail(`Dimension inexistente "${dimensionId}".`);
      if (state.dimensions.some((d) => d.dimensionId === dimensionId)) {
        fail(`La dimension "${resolved.dimension.name}" ya forma parte del analisis.`);
      }
      const taken = keys();
      if (key !== undefined && taken.has(key)) fail(`La clave "${key}" ya esta en uso.`);
      const defaultGrain =
        isTemporalDimension(resolved) && !isCalculatedDimension(resolved.dimension)
          ? resolved.dimension.defaultTimeGrain
          : undefined;
      const grain = timeGrain ?? defaultGrain;
      const dimension: AnalysisDimension = {
        key: key ?? allocateFieldKey(dimensionId, taken),
        dimensionId,
        ...(grain === undefined ? {} : { timeGrain: grain }),
      };
      return { ...state, dimensions: [...state.dimensions, dimension] };
    }
    case 'removeDimension': {
      const { dimensionId } = action.payload;
      dimensionKey(dimensionId);
      const hierarchies = new Set(
        index.model.hierarchies
          .filter((hierarchy) =>
            hierarchy.levels.some((level) => level.dimensionId === dimensionId),
          )
          .map((hierarchy) => hierarchy.id),
      );
      return {
        ...state,
        dimensions: state.dimensions.filter((d) => d.dimensionId !== dimensionId),
        drilldown: state.drilldown.filter((drill) => !hierarchies.has(drill.hierarchyId)),
      };
    }
    case 'addMeasure': {
      const { measureId, key } = action.payload;
      const measure = index.getMeasure(measureId) ?? fail(`Medida inexistente "${measureId}".`);
      if (state.measures.some((m) => m.measureId === measureId)) {
        fail(`La medida "${measure.name}" ya forma parte del analisis.`);
      }
      const taken = keys();
      if (key !== undefined && taken.has(key)) fail(`La clave "${key}" ya esta en uso.`);
      return {
        ...state,
        measures: [
          ...state.measures,
          { key: key ?? allocateFieldKey(measureId, taken), measureId },
        ],
      };
    }
    case 'removeMeasure': {
      measureKey(action.payload.measureId);
      return {
        ...state,
        measures: state.measures.filter((m) => m.measureId !== action.payload.measureId),
      };
    }
    case 'addFilter': {
      const { filter } = action.payload;
      if (state.filters.some((f) => f.id === filter.id))
        fail(`Ya existe un filtro "${filter.id}".`);
      return { ...state, filters: [...state.filters, filter] };
    }
    case 'removeFilter': {
      const filters = state.filters.filter((f) => f.id !== action.payload.filterId);
      if (filters.length === state.filters.length)
        fail(`El filtro "${action.payload.filterId}" no existe.`);
      return { ...state, filters };
    }
    case 'updateFilter': {
      const { filterId, operator, values } = action.payload;
      const current =
        state.filters.find((f) => f.id === filterId) ?? fail(`El filtro "${filterId}" no existe.`);
      if (operator === undefined && values === undefined)
        fail('La actualizacion del filtro no indica cambios.');
      const next = {
        ...current,
        operator: operator ?? current.operator,
        values: values ?? current.values,
      };
      const [min, max] = FILTER_OPERATOR_ARITY[next.operator];
      if (next.values.length < min || next.values.length > max) {
        fail(`El operador "${next.operator}" no admite ${String(next.values.length)} valores.`);
      }
      return { ...state, filters: state.filters.map((f) => (f.id === filterId ? next : f)) };
    }
    case 'clearFilters':
      return state.filters.length === 0 ? state : { ...state, filters: [] };
    case 'sort':
      return {
        ...state,
        sorting: action.payload.by.map(({ target, direction }) => ({
          key:
            target.kind === 'dimension'
              ? dimensionKey(target.dimensionId)
              : measureKey(target.measureId),
          direction,
        })),
      };
    case 'setLimit':
      return state.limit === action.payload.limit
        ? state
        : { ...state, limit: action.payload.limit };
    case 'setVisualization': {
      const { type, encodings } = action.payload;
      const taken = keys();
      for (const fieldKeys of Object.values(encodings ?? {})) {
        for (const key of fieldKeys)
          if (!taken.has(key)) fail(`Clave inexistente "${key}" en la visualizacion.`);
      }
      if (state.visualization.type === type && encodings === undefined) return state;
      return {
        ...state,
        visualization: { ...state.visualization, type, encodings: encodings ?? {} },
      };
    }
    case 'drillDown': {
      const { hierarchyId, value, levelId } = action.payload;
      const hierarchy =
        index.getHierarchy(hierarchyId) ?? fail(`Jerarquia inexistente "${hierarchyId}".`);
      const drill = state.drilldown.find((d) => d.hierarchyId === hierarchyId);
      const depth = drill
        ? hierarchy.levels.findIndex((level) => level.id === drill.levelId)
        : hierarchy.levels.findIndex((level) =>
            state.dimensions.some((d) => d.dimensionId === level.dimensionId),
          );
      const current =
        hierarchy.levels[depth] ??
        fail('Ninguna dimension del analisis pertenece a esa jerarquia.');
      const targetDepth =
        levelId === undefined
          ? depth + 1
          : hierarchy.levels.findIndex((level) => level.id === levelId);
      const target = hierarchy.levels[targetDepth];
      if (!target || targetDepth <= depth) fail('No hay un nivel inferior al que bajar.');
      return {
        ...state,
        drilldown: [
          ...state.drilldown.filter((d) => d.hierarchyId !== hierarchyId),
          {
            hierarchyId,
            levelId: target?.id ?? current.id,
            path: [...(drill?.path ?? []), { levelId: current.id, value }],
          },
        ],
      };
    }
    case 'drillUp': {
      const { hierarchyId } = action.payload;
      const drill =
        state.drilldown.find((d) => d.hierarchyId === hierarchyId) ??
        fail('No hay drilldown activo en esa jerarquia.');
      const rest = state.drilldown.filter((d) => d.hierarchyId !== hierarchyId);
      const last = drill.path.at(-1);
      if (!last || drill.path.length === 1) return { ...state, drilldown: rest };
      return {
        ...state,
        drilldown: [...rest, { hierarchyId, levelId: last.levelId, path: drill.path.slice(0, -1) }],
      };
    }
    case 'comparePeriods':
      return { ...state, comparison: action.payload.comparison };
    case 'resetAnalysis': {
      const fresh = createAnalysisState({ id: state.id, semanticModelId: state.semanticModelId });
      return {
        ...fresh,
        schemaVersion: state.schemaVersion,
        ...(state.name === undefined ? {} : { name: state.name }),
        ...(state.description === undefined ? {} : { description: state.description }),
        interaction: state.interaction,
        layout: state.layout,
        ...(state.metadata === undefined ? {} : { metadata: state.metadata }),
      };
    }
    case 'updateGrouping': {
      const { grandTotal, subtotals, rollupKeys } = action.payload;
      const dimensionKeys = new Set(state.dimensions.map((d) => d.key));
      for (const key of rollupKeys ?? []) {
        if (!dimensionKeys.has(key)) fail(`"${key}" no es una dimension del analisis.`);
      }
      const nextSubtotals = subtotals ?? state.grouping.subtotals;
      const firstKey = state.dimensions[0]?.key;
      return {
        ...state,
        grouping: {
          grandTotal: grandTotal ?? state.grouping.grandTotal,
          subtotals: nextSubtotals,
          rollupKeys:
            rollupKeys ??
            (nextSubtotals
              ? state.grouping.rollupKeys.length > 0
                ? state.grouping.rollupKeys
                : firstKey === undefined
                  ? []
                  : [firstKey]
              : []),
        },
      };
    }
    case 'resetSelections': {
      const { dimensionId } = action.payload;
      const selections =
        dimensionId === undefined
          ? []
          : state.selections.filter((s) => s.dimensionId !== dimensionId);
      return selections.length === state.selections.length ? state : { ...state, selections };
    }
    case 'createMeasure': {
      const { measure } = action.payload;
      modelChanges.push({
        actionId: action.id,
        actionType: 'createMeasure',
        objectId: measure.id,
        objectLabel: measure.label ?? measure.name,
        operation: isCalculatedMeasure(measure)
          ? { op: 'add', collection: 'calculatedMeasures', item: measure }
          : { op: 'add', collection: 'measures', item: measure },
        requiresConfirmation: true,
      });
      return state;
    }
    case 'createDimension': {
      const { dimension } = action.payload;
      modelChanges.push({
        actionId: action.id,
        actionType: 'createDimension',
        objectId: dimension.id,
        objectLabel: dimension.label ?? dimension.name,
        operation: isCalculatedDimension(dimension)
          ? { op: 'add', collection: 'calculatedDimensions', item: dimension }
          : { op: 'add', collection: 'dimensions', item: dimension },
        requiresConfirmation: true,
      });
      return state;
    }
    case 'createCalculatedColumn': {
      const { column } = action.payload;
      modelChanges.push({
        actionId: action.id,
        actionType: 'createCalculatedColumn',
        objectId: column.id,
        objectLabel: column.label ?? column.name,
        operation: { op: 'add', collection: 'calculatedColumns', item: column },
        requiresConfirmation: true,
      });
      return state;
    }
    case 'narrative':
    case 'requestExplanation':
    case 'suggestVisualization':
      informational.push(action);
      return state;
  }
}
